/**
 * Application Configuration
 * General application settings and constants
 */

export const APP_CONFIG = {
  // Application metadata
  APP: {
    NAME: 'Vault 2.0',
    VERSION: '2.0.0',
    DESCRIPTION: 'Audio library management system',
    COPYRIGHT: '© 2024 Vault',
  },

  // Environment settings
  ENVIRONMENT: {
    IS_DEVELOPMENT: process.env.NODE_ENV === 'development',
    IS_PRODUCTION: process.env.NODE_ENV === 'production',
    IS_TEST: process.env.NODE_ENV === 'test',
  },

  // Performance settings
  PERFORMANCE: {
    // Virtual scrolling thresholds
    VIRTUAL_SCROLL_THRESHOLD: 1000,
    
    // Debounce delays
    SEARCH_DEBOUNCE: 300,
    FILTER_DEBOUNCE: 500,
    RESIZE_DEBOUNCE: 100,
    
    // Pagination
    DEFAULT_PAGE_SIZE: 50,
    MAX_PAGE_SIZE: 200,
    
    // Cache settings
    CACHE_DURATION: 300000, // 5 minutes
    MAX_CACHE_SIZE: 100, // items
  },

  // UI behavior settings
  UI: {
    // Toast notifications
    TOAST_DURATION: 5000,
    TOAST_AUTO_DISMISS: true,
    
    // Modal settings
    MODAL_BACKDROP_CLOSE: true,
    MODAL_ESC_CLOSE: true,
    
    // Loading states
    MIN_LOADING_TIME: 500, // Minimum time to show loader
    LOADING_TIMEOUT: 30000, // Max time before showing error
    
    // Transitions
    PAGE_TRANSITION_DURATION: 300,
    COMPONENT_TRANSITION_DURATION: 200,
  },

  // Feature flags
  FEATURES: {
    CHATBOT_ENABLED: true,
    AI_SUGGESTIONS: true,
    BATCH_UPLOAD: true,
    ADVANCED_FILTERS: true,
    REAL_TIME_UPDATES: false,
    OFFLINE_MODE: false,
    DARK_MODE: false,
    ANALYTICS: process.env.NODE_ENV === 'production',
  },

  // Date and time settings
  DATE_TIME: {
    DEFAULT_FORMAT: 'YYYY-MM-DD',
    DISPLAY_FORMAT: 'MMM DD, YYYY',
    TIME_FORMAT: 'HH:mm:ss',
    TIMEZONE: 'UTC',
    
    // Date range defaults
    DEFAULT_DATE_RANGE: {
      FROM: 439689600000, // December 8, 1983
      TO: Date.now(),
    },
  },

  // Search and filtering
  SEARCH: {
    MIN_QUERY_LENGTH: 2,
    MAX_QUERY_LENGTH: 100,
    MAX_RESULTS: 1000,
    HIGHLIGHT_MATCHES: true,
    FUZZY_SEARCH: true,
    SEARCH_FIELDS: [
      'trackName',
      'producer',
      'agency',
      'client',
      'description',
      'tags',
      'genres',
    ],
  },

  // User roles and permissions
  ROLES: {
    USER: 'USER',
    OPERATOR: 'OPERATOR',
    ADMIN: 'ADMIN',
  },

  // Collection names (references to existing constants)
  COLLECTIONS: {
    // Note: These should reference the existing constants/paths.js
    // This is just for documentation - actual values should come from paths.js
    AUDIO_META: 'audiometa',
    TAGS: 'tags',
    GENRES: 'genres',
    USERS: 'users',
    PLAYLISTS: 'playlists',
  },

  // Local storage keys
  STORAGE_KEYS: {
    USER_PREFERENCES: 'vault_user_preferences',
    FILTER_STATE: 'vault_filter_state',
    COLUMN_WIDTHS: 'vault_column_widths',
    THEME: 'vault_theme',
    LAST_VISIT: 'vault_last_visit',
  },

  // URLs and links
  URLS: {
    DOCUMENTATION: 'https://docs.vault.com',
    SUPPORT: 'https://support.vault.com',
    PRIVACY_POLICY: 'https://vault.com/privacy',
    TERMS_OF_SERVICE: 'https://vault.com/terms',
  },

  // Development tools
  DEV_TOOLS: {
    ENABLE_REDUX_DEVTOOLS: process.env.NODE_ENV === 'development',
    ENABLE_WHY_DID_YOU_RENDER: false,
    ENABLE_PERFORMANCE_MONITORING: false,
    LOG_LEVEL: process.env.NODE_ENV === 'development' ? 'debug' : 'error',
  },
};

export default APP_CONFIG;